import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { currentUserId } from '@/lib/currentUser';
import { AppLayout } from '@/components/layout/AppLayout';
import { PageHeader } from '@/components/ui/page-header';
import { DataTable } from '@/components/ui/data-table';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { SCI_MANAGERS } from '@/lib/managers';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from '@/components/ui/dropdown-menu';
import { Badge } from '@/components/ui/badge';
import { Plus, Building2, Trash2, CheckCircle2, XCircle, ExternalLink, MoreVertical, Edit, Eye, MapPin, CreditCard, FileText, Loader2 } from 'lucide-react';
import { ImportSCIsDialog } from '@/components/scis/ImportSCIsDialog';
import { PaymentAccounts } from '@/components/scis/PaymentAccounts';
import { PappersLookup, type PappersLookupResult } from '@/components/ui/PappersLookup';
import { toast } from 'sonner';
import { validateSiret, validatePostalCode } from '@/lib/validators';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { useSearchParams } from 'react-router-dom';
import { backupAuthSession } from '@/hooks/useAuth';
import { isValidIban } from '../../supabase/functions/_shared/payment-account.ts';

const managerFromForm = (formData: FormData) => {
  const value = formData.get('manager') as string;
  return value && value !== 'none' ? value : null;
};

export default function SCIs() {
  const [isOpen, setIsOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [selectedSCI, setSelectedSCI] = useState<any>(null);
  const [editingSCI, setEditingSCI] = useState<any>(null);
  const [siretError, setSiretError] = useState<string>();
  const [postalCodeError, setPostalCodeError] = useState<string>();
  const [pappersData, setPappersData] = useState<PappersLookupResult | null>(null);
  const [editPappersData, setEditPappersData] = useState<PappersLookupResult | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [newManager, setNewManager] = useState<string>('none');
  const [editManager, setEditManager] = useState<string>('none');
  const [bulkManagerLoading, setBulkManagerLoading] = useState(false);

  useEffect(() => {
    setEditManager(editingSCI?.manager || 'none');
  }, [editingSCI]);
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [bulkDeleteLoading, setBulkDeleteLoading] = useState(false);
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();

  const { data: scis = [], isLoading } = useQuery({
    queryKey: ['scis'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('scis')
        .select('*, clients(name)')
        .order('created_at', { ascending: false });
      if (error) throw error;
      return data;
    },
  });

  // Handle OAuth callback success/error and open the connected SCI
  useEffect(() => {
    const success = searchParams.get('superpdp_success');
    const error = searchParams.get('superpdp_error');
    const sciId = searchParams.get('sci_id');

    if (!success && !error) return;
    if (sciId && isLoading) return;

    if (success === 'true') {
      toast.success('SUPER PDP connecté avec succès pour cette SCI');
      queryClient.invalidateQueries({ queryKey: ['scis'] });
      const connectedSci = scis.find((sci) => sci.id === sciId);
      if (connectedSci) {
        setSelectedSCI(connectedSci);
      }
    } else if (error) {
      toast.error(`Erreur de connexion SUPER PDP: ${decodeURIComponent(error)}`);
    }

    setSearchParams({});
  }, [searchParams, queryClient, setSearchParams, scis, isLoading]);

  useEffect(() => {
    if (!selectedSCI?.id) return;
    const updated = scis.find((sci) => sci.id === selectedSCI.id);
    if (updated) {
      setSelectedSCI(updated);
    }
  }, [scis, selectedSCI?.id]);

  // Fetch SUPER PDP configuration to check if OAuth is available
  const { data: superpdpConfig } = useQuery({
    queryKey: ['superpdp-config-for-oauth'],
    queryFn: async () => {
      const { data: profile } = await supabase
        .from('profiles')
        .select('organization_id')
        .eq('user_id', await currentUserId())
        .single();

      if (!profile?.organization_id) return null;

      const { data, error } = await supabase
        .from('superpdp_configurations')
        .select('superpdp_client_id, is_active')
        .eq('organization_id', profile.organization_id)
        .maybeSingle();
      
      if (error) throw error;
      return data;
    },
  });

  const { data: clients = [] } = useQuery({
    queryKey: ['clients'],
    queryFn: async () => {
      const { data, error } = await supabase.from('clients').select('id, name');
      if (error) throw error;
      return data;
    },
  });

  const createSCI = useMutation({
    mutationFn: async (formData: FormData) => {
      const siret = formData.get('siret') as string;
      const siretValidation = validateSiret(siret);
      if (!siretValidation.valid) {
        throw new Error(siretValidation.message);
      }

      const postalCode = formData.get('postal_code') as string;
      const postalValidation = validatePostalCode(postalCode);
      if (!postalValidation.valid) {
        throw new Error(postalValidation.message);
      }

      const { data: profile } = await supabase
        .from('profiles')
        .select('organization_id')
        .eq('user_id', await currentUserId())
        .single();

      if (!profile?.organization_id) throw new Error('No organization');

      const iban = String(formData.get('iban') || '').trim();
      if (iban && !isValidIban(iban)) throw new Error('IBAN invalide');

      const { error } = await supabase.from('scis').insert({
        organization_id: profile.organization_id,
        client_id: formData.get('client_id') as string,
        name: formData.get('name') as string,
        siret: formData.get('siret') as string || null,
        address: formData.get('address') as string || null,
        postal_code: formData.get('postal_code') as string || null,
        city: formData.get('city') as string || null,
        iban: iban || null,
        bic: formData.get('bic') as string || null,
        signature_name: formData.get('signature_name') as string || null,
        receipt_prefix: formData.get('receipt_prefix') as string || null,
        vat_number: formData.get('vat_number') as string || null,
        manager: managerFromForm(formData),
      });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['scis'] });
      setIsOpen(false);
      setSiretError(undefined);
      setPostalCodeError(undefined);
      setPappersData(null);
      setNewManager('none');
      toast.success('SCI créée avec succès');
    },
    onError: (error: any) => {
      toast.error(error.message);
    },
  });

  const updateSCI = useMutation({
    mutationFn: async (formData: FormData) => {
      if (!editingSCI?.id) throw new Error('No SCI selected');

      const siret = formData.get('siret') as string;
      if (siret) {
        const siretValidation = validateSiret(siret);
        if (!siretValidation.valid) {
          throw new Error(siretValidation.message);
        }
      }

      const postalCode = formData.get('postal_code') as string;
      if (postalCode) {
        const postalValidation = validatePostalCode(postalCode);
        if (!postalValidation.valid) {
          throw new Error(postalValidation.message);
        }
      }

      const { error } = await supabase
        .from('scis')
        .update({
          client_id: formData.get('client_id') as string,
          name: formData.get('name') as string,
          siret: formData.get('siret') as string || null,
          address: formData.get('address') as string || null,
          postal_code: formData.get('postal_code') as string || null,
          city: formData.get('city') as string || null,
          signature_name: formData.get('signature_name') as string || null,
          receipt_prefix: formData.get('receipt_prefix') as string || null,
          vat_number: formData.get('vat_number') as string || null,
          manager: managerFromForm(formData),
        })
        .eq('id', editingSCI.id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['scis'] });
      setEditingSCI(null);
      setIsEditOpen(false);
      setSelectedSCI(null);
      setSiretError(undefined);
      setPostalCodeError(undefined);
      setEditPappersData(null);
      toast.success('SCI mis à jour avec succès');
    },
    onError: (error: any) => {
      toast.error(error.message);
    },
  });

  const deleteSCI = useMutation({
    mutationFn: async (sciId: string) => {
      const { error } = await supabase
        .from('scis')
        .delete()
        .eq('id', sciId);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['scis'] });
      toast.success('SCI supprimée avec succès');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Erreur lors de la suppression');
    },
  });

  const handleSiretChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value) {
      const validation = validateSiret(value);
      setSiretError(validation.valid ? undefined : validation.message);
    } else {
      setSiretError(undefined);
    }
  };

  const handlePostalCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value) {
      const validation = validatePostalCode(value);
      setPostalCodeError(validation.valid ? undefined : validation.message);
    } else {
      setPostalCodeError(undefined);
    }
  };

  // Initiate SUPER PDP OAuth flow
  const initiateSuperPDPOAuth = async (sciId: string, siret: string) => {
    if (!siret) {
      toast.error('Un SIRET est requis pour connecter cette SCI à SUPER PDP');
      return;
    }

    if (!superpdpConfig?.superpdp_client_id || !superpdpConfig?.is_active) {
      toast.error('SUPER PDP n\'est pas configuré. Veuillez configurer SUPER PDP dans les paramètres d\'intégration.');
      return;
    }

    try {
      // Get Supabase URL for callback
      // VITE_SUPABASE_URL can be: 
      //   - https://[project-ref].supabase.co/rest/v1
      //   - https://[project-ref].supabase.co
      // We need: https://[project-ref].supabase.co/functions/v1/superpdp-oauth-callback
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      if (!supabaseUrl) {
        throw new Error('VITE_SUPABASE_URL is not configured');
      }
      
      // Construct the callback URL properly
      // Remove /rest/v1 if present, remove any trailing slashes, then add /functions/v1
      let baseUrl = supabaseUrl.replace('/rest/v1', '').replace(/\/$/, '');
      // Ensure we have the base URL without any path
      const urlObj = new URL(baseUrl);
      baseUrl = `${urlObj.protocol}//${urlObj.host}`;
      const callbackUrl = `${baseUrl}/functions/v1/superpdp-oauth-callback`;
      
      console.log('OAuth redirect_uri:', callbackUrl);
      console.log('⚠️ IMPORTANT: Make sure this exact URL is registered in SUPER PDP OAuth app settings');
      console.log('   Go to SUPER PDP → OAuth App Settings → Redirect URIs');
      console.log('   Add:', callbackUrl);
      
      const state = encodeURIComponent(JSON.stringify({
        sci_id: sciId,
      }));
      
      // Build OAuth authorization URL
      // Do not prefill superpdp_company_number: SuperPDP 400s if that SIREN
      // is not already onboarded for this OAuth app / sandbox|prod env.
      const authUrl = new URL('https://api.superpdp.tech/oauth2/authorize');
      authUrl.searchParams.set('client_id', superpdpConfig.superpdp_client_id);
      authUrl.searchParams.set('redirect_uri', callbackUrl);
      authUrl.searchParams.set('response_type', 'code');
      authUrl.searchParams.set('state', state);
      
      // Log the full URL for debugging
      console.log('OAuth authorization URL:', authUrl.toString());

      await backupAuthSession();
      window.location.href = authUrl.toString();
    } catch (error: any) {
      console.error('OAuth initiation error:', error);
      toast.error(`Erreur lors de l'initialisation de la connexion: ${error.message}`);
    }
  };

  // Check if SCI has valid SUPER PDP token
  const hasSuperPDPToken = (sci: any): boolean => {
    if (!sci.superpdp_token || !sci.superpdp_token_expires_at) return false;
    const expiresAt = new Date(sci.superpdp_token_expires_at);
    return new Date() < expiresAt;
  };

  const columns = [
    { header: 'Nom', accessorKey: 'name' as const },
    { header: 'Client', accessorKey: (row: any) => row.clients?.name || '-' },
    { header: 'SIRET', accessorKey: 'siret' as const },
    { header: 'Adresse', accessorKey: 'address' as const },
    { header: 'CP', accessorKey: 'postal_code' as const },
    { header: 'Ville', accessorKey: 'city' as const },
    { header: 'Gestionnaire', accessorKey: (row: any) => row.manager || '-' },
    { 
      header: 'SUPER PDP', 
      accessorKey: (row: any) => {
        const hasToken = hasSuperPDPToken(row);
        return (
          <div className="flex items-center gap-2">
            {hasToken ? (
              <Badge variant="outline" className="gap-1 text-green-600 border-green-600">
                <CheckCircle2 className="h-3 w-3" />
                Connecté
              </Badge>
            ) : (
              <Badge variant="outline" className="gap-1 text-muted-foreground">
                <XCircle className="h-3 w-3" />
                Non connecté
              </Badge>
            )}
          </div>
        );
      }
    },
    { header: 'IBAN', accessorKey: (row: any) => row.iban ? `***${row.iban.slice(-4)}` : '-' },
    {
      header: 'Actions',
      accessorKey: (row: any) => (
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setSelectedSCI(row)}
            className="gap-2"
          >
            <Eye className="h-4 w-4" />
            Détails
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm">
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => {
                setEditingSCI(row);
                setIsEditOpen(true);
              }}>
                <Edit className="mr-2 h-4 w-4" />
                Modifier
              </DropdownMenuItem>
              {superpdpConfig?.is_active && row.siret && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => initiateSuperPDPOAuth(row.id, row.siret)}>
                    <ExternalLink className="mr-2 h-4 w-4" />
                    {hasSuperPDPToken(row) ? 'Reconnecter SUPER PDP' : 'Connecter SUPER PDP'}
                  </DropdownMenuItem>
                </>
              )}
              <DropdownMenuSeparator />
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <DropdownMenuItem onSelect={(e) => e.preventDefault()} className="text-destructive">
                    <Trash2 className="mr-2 h-4 w-4" />
                    Supprimer
                  </DropdownMenuItem>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Supprimer la SCI</AlertDialogTitle>
                    <AlertDialogDescription>
                      Êtes-vous sûr de vouloir supprimer la SCI "{row.name}" ? Cette action est irréversible et supprimera également tous les biens, contrats et données associés.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Annuler</AlertDialogCancel>
                    <AlertDialogAction
                      onClick={() => deleteSCI.mutate(row.id)}
                      disabled={deleteSCI.isPending}
                      className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                    >
                      {deleteSCI.isPending ? 'Suppression...' : 'Supprimer'}
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      ),
    },
  ];

  return (
    <AppLayout title="SCI" description="Gérez vos Sociétés Civiles Immobilières">
      <PageHeader
        title="SCI"
        description={`${scis.length} SCI`}
        actions={
          <div className="flex gap-2">
            <ImportSCIsDialog />
            <Dialog open={isOpen} onOpenChange={setIsOpen}>
              <DialogTrigger asChild>
                <Button><Plus className="mr-2 h-4 w-4" /> Nouvelle SCI</Button>
              </DialogTrigger>
            <DialogContent className="max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Nouvelle SCI</DialogTitle>
              </DialogHeader>
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  const fd = new FormData(e.currentTarget);
                  fd.set('manager', newManager);
                  createSCI.mutate(fd);
                }}
                className="space-y-4"
              >
                <div className="space-y-2">
                  <Label>Client *</Label>
                  <Select name="client_id" required>
                    <SelectTrigger>
                      <SelectValue placeholder="Sélectionner un client" />
                    </SelectTrigger>
                    <SelectContent>
                      {clients.map((client) => (
                        <SelectItem key={client.id} value={client.id}>{client.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="name">Nom de la SCI *</Label>
                  <Input id="name" name="name" required defaultValue={pappersData?.name || ''} key={pappersData?.name || 'name'} />
                </div>
                <div className="space-y-2">
                  <Label>Rechercher via Pappers</Label>
                  <PappersLookup
                    onSelect={(result) => {
                      setPappersData(result);
                    }}
                    placeholder="Rechercher le SIRET/TVA via Pappers..."
                  />
                  <p className="text-xs text-muted-foreground">Remplissage automatique du SIRET, TVA, et adresse</p>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="siret">SIRET</Label>
                    <Input 
                      id="siret" 
                      name="siret" 
                      maxLength={14} 
                      placeholder="14 chiffres"
                      defaultValue={pappersData?.siret || ''}
                      key={pappersData?.siret || 'siret'}
                      onChange={handleSiretChange}
                    />
                    {siretError && <p className="text-sm text-destructive">{siretError}</p>}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="vat_number">N° TVA Intracommunautaire</Label>
                    <Input 
                      id="vat_number" 
                      name="vat_number" 
                      placeholder="FRXX XXXXXXXXX"
                      maxLength={15}
                      defaultValue={pappersData?.vatNumber || ''}
                      key={pappersData?.vatNumber || 'vat'}
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="signature_name">Nom signataire</Label>
                  <Input id="signature_name" name="signature_name" />
                </div>
                <div className="space-y-2">
                  <Label>Gestionnaire</Label>
                  <Select value={newManager} onValueChange={setNewManager}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Aucun</SelectItem>
                      {SCI_MANAGERS.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="address">Adresse</Label>
                  <Input id="address" name="address" defaultValue={pappersData?.address || ''} key={pappersData?.address || 'addr'} />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="postal_code">Code postal</Label>
                    <Input 
                      id="postal_code" 
                      name="postal_code" 
                      maxLength={5} 
                      placeholder="75001"
                      defaultValue={pappersData?.postalCode || ''}
                      key={pappersData?.postalCode || 'pc'}
                      onChange={handlePostalCodeChange}
                    />
                    {postalCodeError && <p className="text-sm text-destructive">{postalCodeError}</p>}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="city">Ville</Label>
                    <Input id="city" name="city" defaultValue={pappersData?.city || ''} key={pappersData?.city || 'city'} />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="iban">IBAN</Label>
                  <Input id="iban" name="iban" placeholder="FR76 1234 5678 9012 3456 7890 123" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="bic">BIC</Label>
                    <Input id="bic" name="bic" placeholder="ABCDEFGH" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="receipt_prefix">Préfixe documents</Label>
                    <Input 
                      id="receipt_prefix" 
                      name="receipt_prefix" 
                      placeholder="QUI" 
                      maxLength={10}
                    />
                    <p className="text-xs text-muted-foreground">Format: PREFIX-2026-00001</p>
                  </div>
                </div>
                <Button type="submit" className="w-full" disabled={createSCI.isPending || !!siretError || !!postalCodeError}>
                  {createSCI.isPending ? 'Création...' : 'Créer la SCI'}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
          </div>
        }
      />
      <DataTable
        columns={columns}
        data={scis}
        isLoading={isLoading}
        emptyMessage="Aucune SCI. Créez d'abord un client, puis ajoutez une SCI."
        selectable
        selectedIds={selectedIds}
        onSelectionChange={setSelectedIds}
      />

      {/* Bulk Delete Bar */}
      {selectedIds.size > 0 && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 bg-background border rounded-lg shadow-lg px-4 py-3 flex items-center gap-4">
          <span className="text-sm font-medium">{selectedIds.size} sélectionné(s)</span>
          <Select
            value=""
            disabled={bulkManagerLoading}
            onValueChange={async (value) => {
              setBulkManagerLoading(true);
              const { error } = await supabase
                .from('scis')
                .update({ manager: value === 'none' ? null : value })
                .in('id', Array.from(selectedIds));
              setBulkManagerLoading(false);
              if (error) {
                toast.error(error.message);
                return;
              }
              queryClient.invalidateQueries({ queryKey: ['scis'] });
              toast.success(`Gestionnaire mis à jour pour ${selectedIds.size} SCI(s)`);
              setSelectedIds(new Set());
            }}
          >
            <SelectTrigger className="w-56 h-9">
              <SelectValue placeholder={bulkManagerLoading ? 'Mise à jour...' : 'Assigner un gestionnaire'} />
            </SelectTrigger>
            <SelectContent>
              {SCI_MANAGERS.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
              <SelectItem value="none">Aucun</SelectItem>
            </SelectContent>
          </Select>
          <AlertDialog open={bulkDeleteOpen} onOpenChange={(open) => !bulkDeleteLoading && setBulkDeleteOpen(open)}>
            <AlertDialogTrigger asChild>
              <Button variant="destructive" size="sm"><Trash2 className="h-4 w-4 mr-2" /> Supprimer</Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Supprimer {selectedIds.size} SCI(s) ?</AlertDialogTitle>
                <AlertDialogDescription asChild>
                  <div className="space-y-2">
                    <p>Cette action est <strong>irréversible</strong>.</p>
                    <p className="text-destructive font-medium">⚠️ Toutes les données liées seront supprimées :</p>
                    <ul className="list-disc list-inside text-sm space-y-1">
                      <li>Biens immobiliers et leurs documents</li>
                      <li>Contrats de location</li>
                      <li>Périodes de loyer, paiements et allocations</li>
                      <li>Quittances et appels de fonds</li>
                      <li>Connexions bancaires et transactions</li>
                    </ul>
                    <p className="text-sm text-muted-foreground">Les locataires liés ne seront pas supprimés mais dissociés de la SCI.</p>
                  </div>
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel disabled={bulkDeleteLoading}>Annuler</AlertDialogCancel>
                <AlertDialogAction
                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                  disabled={bulkDeleteLoading}
                  onClick={async () => {
                    setBulkDeleteLoading(true);
                    let successCount = 0;
                    const totalCount = selectedIds.size;
                    try {
                      for (const id of selectedIds) {
                        try {
                          // Unlink tenants first (tenants.sci_id has ON DELETE RESTRICT)
                          await supabase.from('tenants').update({ sci_id: null }).eq('sci_id', id);
                          const { error } = await supabase.from('scis').delete().eq('id', id);
                          if (error) throw error;
                          successCount++;
                        } catch (err: any) {
                          console.error(`Échec suppression SCI ${id}:`, err);
                          toast.error(`Erreur sur une SCI : ${err.message || 'Erreur inconnue'}`);
                        }
                      }
                      queryClient.invalidateQueries({ queryKey: ['scis'] });
                      queryClient.invalidateQueries({ queryKey: ['properties'] });
                      queryClient.invalidateQueries({ queryKey: ['leases'] });
                      queryClient.invalidateQueries({ queryKey: ['tenants'] });
                      queryClient.invalidateQueries({ queryKey: ['receipts'] });
                      queryClient.invalidateQueries({ queryKey: ['call_for_funds'] });
                      queryClient.invalidateQueries({ queryKey: ['payments'] });
                      queryClient.invalidateQueries({ queryKey: ['rent_periods'] });
                      if (successCount === totalCount) {
                        toast.success(`${successCount} SCI(s) supprimée(s)`);
                      } else if (successCount > 0) {
                        toast.warning(`${successCount}/${totalCount} SCI(s) supprimée(s). ${totalCount - successCount} erreur(s).`);
                      }
                      setSelectedIds(new Set());
                    } finally {
                      setBulkDeleteLoading(false);
                      setBulkDeleteOpen(false);
                    }
                  }}
                >
                  {bulkDeleteLoading ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Suppression...</> : 'Supprimer'}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
          <Button variant="outline" size="sm" onClick={() => setSelectedIds(new Set())} disabled={bulkDeleteLoading}>Annuler</Button>
        </div>
      )}

      {/* Details Sheet */}
      <Sheet open={!!selectedSCI} onOpenChange={() => setSelectedSCI(null)}>
        <SheetContent className="w-full sm:max-w-xl overflow-y-auto">
          {selectedSCI && (
            <>
              <SheetHeader>
                <SheetTitle className="flex items-center gap-2">
                  <Building2 className="h-5 w-5" />
                  {selectedSCI.name}
                </SheetTitle>
              </SheetHeader>
              <div className="space-y-4 mt-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Informations générales</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div>
                      <p className="text-sm text-muted-foreground">Client</p>
                      <p className="font-medium">{selectedSCI.clients?.name || '-'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">SIRET</p>
                      <p className="font-medium">{selectedSCI.siret || '-'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">N° TVA Intracommunautaire</p>
                      <p className="font-medium">{selectedSCI.vat_number || '-'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Nom signataire</p>
                      <p className="font-medium">{selectedSCI.signature_name || '-'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Gestionnaire</p>
                      <p className="font-medium">{selectedSCI.manager || '-'}</p>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <MapPin className="h-4 w-4" />
                      Adresse
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div>
                      <p className="text-sm text-muted-foreground">Adresse</p>
                      <p className="font-medium">{selectedSCI.address || '-'}</p>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm text-muted-foreground">Code postal</p>
                        <p className="font-medium">{selectedSCI.postal_code || '-'}</p>
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">Ville</p>
                        <p className="font-medium">{selectedSCI.city || '-'}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <CreditCard className="h-4 w-4" />
                      Informations bancaires
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <PaymentAccounts key={selectedSCI.id} sciId={selectedSCI.id} organizationId={selectedSCI.organization_id} />
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <FileText className="h-4 w-4" />
                      Numérotation documents
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div>
                      <p className="text-sm text-muted-foreground">Préfixe</p>
                      <p className="font-medium">{selectedSCI.receipt_prefix || 'QUI (par défaut)'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Format</p>
                      <p className="font-medium text-xs text-muted-foreground">{selectedSCI.receipt_prefix || 'QUI'}-2026-00001</p>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">SUPER PDP</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center gap-2">
                      {hasSuperPDPToken(selectedSCI) ? (
                        <Badge variant="outline" className="gap-1 text-green-600 border-green-600">
                          <CheckCircle2 className="h-3 w-3" />
                          Connecté
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="gap-1 text-muted-foreground">
                          <XCircle className="h-3 w-3" />
                          Non connecté
                        </Badge>
                      )}
                    </div>
                  </CardContent>
                </Card>

                <div className="flex gap-2">
                  <Button
                    onClick={() => {
                      setEditingSCI(selectedSCI);
                      setIsEditOpen(true);
                    }}
                    className="flex-1"
                  >
                    <Edit className="mr-2 h-4 w-4" />
                    Modifier
                  </Button>
                </div>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>

      {/* Edit Dialog */}
      <Dialog open={isEditOpen} onOpenChange={(open) => {
        setIsEditOpen(open);
        if (!open) {
          setEditingSCI(null);
          setSiretError(undefined);
          setPostalCodeError(undefined);
        }
      }}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Modifier la SCI</DialogTitle>
          </DialogHeader>
          {editingSCI && (
            <form
              onSubmit={(e) => {
                e.preventDefault();
                const fd = new FormData(e.currentTarget);
                fd.set('manager', editManager);
                updateSCI.mutate(fd);
              }}
              className="space-y-4"
            >
              <div className="space-y-2">
                <Label>Client *</Label>
                <Select name="client_id" required defaultValue={editingSCI.client_id}>
                  <SelectTrigger>
                    <SelectValue placeholder="Sélectionner un client" />
                  </SelectTrigger>
                  <SelectContent>
                    {clients.map((client) => (
                      <SelectItem key={client.id} value={client.id}>{client.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-name">Nom de la SCI *</Label>
                <Input id="edit-name" name="name" defaultValue={editPappersData?.name || editingSCI.name} key={editPappersData?.name || editingSCI.name} required />
              </div>
              <div className="space-y-2">
                <Label>Rechercher via Pappers</Label>
                <PappersLookup
                  onSelect={(result) => {
                    setEditPappersData(result);
                  }}
                  placeholder="Rechercher le SIRET/TVA via Pappers..."
                />
                <p className="text-xs text-muted-foreground">Remplissage automatique du SIRET, TVA, et adresse</p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="edit-siret">SIRET</Label>
                  <Input
                    id="edit-siret"
                    name="siret"
                    maxLength={14}
                    placeholder="14 chiffres"
                    defaultValue={editPappersData?.siret || editingSCI.siret || ''}
                    key={editPappersData?.siret || editingSCI.siret || 'edit-siret'}
                    onChange={handleSiretChange}
                  />
                  {siretError && <p className="text-sm text-destructive">{siretError}</p>}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-vat_number">N° TVA Intracommunautaire</Label>
                  <Input
                    id="edit-vat_number"
                    name="vat_number"
                    placeholder="FRXX XXXXXXXXX"
                    maxLength={15}
                    defaultValue={editPappersData?.vatNumber || editingSCI.vat_number || ''}
                    key={editPappersData?.vatNumber || editingSCI.vat_number || 'edit-vat'}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-signature_name">Nom signataire</Label>
                <Input id="edit-signature_name" name="signature_name" defaultValue={editingSCI.signature_name || ''} />
              </div>
              <div className="space-y-2">
                <Label>Gestionnaire</Label>
                <Select value={editManager} onValueChange={setEditManager}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Aucun</SelectItem>
                    {SCI_MANAGERS.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-address">Adresse</Label>
                <Input id="edit-address" name="address" defaultValue={editPappersData?.address || editingSCI.address || ''} key={editPappersData?.address || editingSCI.address || 'edit-addr'} />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="edit-postal_code">Code postal</Label>
                  <Input
                    id="edit-postal_code"
                    name="postal_code"
                    maxLength={5}
                    placeholder="75001"
                    defaultValue={editPappersData?.postalCode || editingSCI.postal_code || ''}
                    key={editPappersData?.postalCode || editingSCI.postal_code || 'edit-pc'}
                    onChange={handlePostalCodeChange}
                  />
                  {postalCodeError && <p className="text-sm text-destructive">{postalCodeError}</p>}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-city">Ville</Label>
                  <Input id="edit-city" name="city" defaultValue={editPappersData?.city || editingSCI.city || ''} key={editPappersData?.city || editingSCI.city || 'edit-city'} />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-receipt_prefix">Préfixe documents</Label>
                <Input id="edit-receipt_prefix" name="receipt_prefix"
                  defaultValue={editingSCI.receipt_prefix || ''} placeholder="QUI" maxLength={10} />
                <p className="text-xs text-muted-foreground">Format: PREFIX-2026-00001</p>
              </div>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  className="flex-1"
                  onClick={() => {
                    setIsEditOpen(false);
                    setEditingSCI(null);
                    setSiretError(undefined);
                    setPostalCodeError(undefined);
                  }}
                >
                  Annuler
                </Button>
                <Button
                  type="submit"
                  className="flex-1"
                  disabled={updateSCI.isPending || !!siretError || !!postalCodeError}
                >
                  {updateSCI.isPending ? 'Mise à jour...' : 'Mettre à jour'}
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </AppLayout>
  );
}
